import { MouseEvent } from "react";
import { Link } from "react-router-dom";
import { cn } from "@tellescope/design-system";
import { formatName, type Name } from "@tellescope/utils";
import { AvatarWithPicture } from "@tellescope/contacts";

export type TicketsListItemOwner = Name & {
  id?: string;
  avatar?: string;
};

export type TicketsListItemProps = {
  title: string;
  owner?: TicketsListItemOwner;
  onOpen?: (() => void) | { to: string; replace?: boolean };
  className?: string;
};

export const TicketsListItem = ({ title, owner, onOpen, className: classNameProp }: TicketsListItemProps) => {
  const interactive = onOpen !== undefined;

  const className = cn(
    "w-full text-left p-3 rounded-md border bg-white transition-colors block",
    interactive && "hover:bg-muted/50 cursor-pointer",
    classNameProp
  );

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (typeof onOpen === "function") onOpen();
  };

  const content = (
    <div className="flex justify-between items-center gap-2">
      <div className="font-medium truncate text-sm min-w-0">{title}</div>
      {owner && (
        <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
          <AvatarWithPicture avatarLike={owner} className="size-5" />
          <span className="truncate">{formatName(owner)}</span>
        </div>
      )}
    </div>
  );

  if (typeof onOpen === "object") {
    return (
      <Link
        to={onOpen.to}
        replace={onOpen.replace}
        onClick={handleClick}
        className={className}
        data-testid="contact-details-block-item"
      >
        {content}
      </Link>
    );
  }

  if (typeof onOpen === "function") {
    return (
      <button onClick={handleClick} className={className} data-testid="contact-details-block-item">
        {content}
      </button>
    );
  }

  return (
    <div className={className} data-testid="contact-details-block-item">
      {content}
    </div>
  );
};
